import { Mail } from 'lucide-react';
import React, { useCallback, useState } from 'react';

import { colors, typography } from '@pray/shared/components/foundations';
import AppBar from '@pray/shared/components/ui/AppBar/AppBar';
import Button from '@pray/shared/components/ui/Button/Button';
import { ChevronLeftMd } from '@pray/shared/components/ui/Icons/ChevronLeftMd';
import PrayLogoPrimary from '@pray/shared/components/ui/PrayLogo/PrayLogoPrimary';
import Text from '@pray/shared/components/ui/Text/Text';
import { REGISTER_VIEW_NAMES, PRAY_EXTERNAL_LINKS, EVENTS } from '@pray/shared/constants';
import { getEmailStatus } from '@pray/shared/utils/auth';
import logger from '@pray/shared/utils/logger';
import storage from '@pray/shared/utils/storage';

import type { LoginOptionsPageProps } from './types';
import SocialAuth from '../../SocialAuth/SocialAuth';
import EmailCapturePage from '../EmailCapturePage/EmailCapturePage';
import EmailConfirmationPage from '../EmailConfirmationPage/EmailConfirmationPage';

import styles from './LoginOptionsPage.module.css';

const Steps = {
  LOGIN_OPTION: 1,
  EMAIL_CAPTURE: 2,
  MAGIC_LINK_SENT: 3,
};

export default function LoginOptionsPage({
  title = 'Sign up to get started!',
  subtitle = "Let's get you signed up.",
  logo = <PrayLogoPrimary />,
  appBarRightComponent = null,
  registerViewName = '',
  route = '/discover/',
  isWebSubscribeFlow = true,
  isShowAppBar = true,
  isShowLogo = true,
  disabled = false,
  isShowSubtitle = false,
  onBack = null,
  onUpdateEmail = null,
  onSuccess = null,
}: LoginOptionsPageProps) {
  const user = storage.getUser();
  const [email, setEmail] = useState(user?.email || '');
  const [step, setStep] = useState(Steps.LOGIN_OPTION);

  const handleEmailValidation = async (): Promise<boolean> => {
    try {
      const [customRoute, queryParams] = route.split('?');
      const result = await getEmailStatus({
        email,
        customRoute,
        queryParams,
        isWebSubscribeFlow,
        registerViewName: registerViewName || REGISTER_VIEW_NAMES.SUBSCRIBE_FLOW,
      });

      const isEmailAvailable = Boolean(result?.isEmailAvailable);

      if (onUpdateEmail) onUpdateEmail(email);

      if (!isEmailAvailable) {
        setStep(Steps.MAGIC_LINK_SENT);
      } else if (onSuccess) {
        onSuccess(isEmailAvailable);
      }

      return isEmailAvailable;
    } catch (error) {
      logger.debug('error', error);
    }

    return false;
  };

  const handleDataChange = (name: string, value: string | boolean) => {
    if (name === 'email') setEmail(value as string);
  };

  const getSubtitle = () => {
    return isShowSubtitle ? subtitle : null;
  };

  const handleEmailLogin = () => {
    setStep(Steps.EMAIL_CAPTURE);
  };

  const handleBackClick = useCallback(() => {
    if (step === Steps.EMAIL_CAPTURE) {
      setStep(Steps.LOGIN_OPTION);
    } else if (step === Steps.MAGIC_LINK_SENT) {
      setStep(Steps.EMAIL_CAPTURE);
    } else {
      onBack?.();
    }
  }, [onBack, step]);

  const appBarLeftComponent =
    !onBack && step === Steps.LOGIN_OPTION ? undefined : (
      <Button onClick={handleBackClick}>
        <ChevronLeftMd />
      </Button>
    );

  const appBarCenterComponent = isShowLogo && logo;

  const renderStep = () => {
    switch (step) {
      case Steps.LOGIN_OPTION:
        return (
          <div
            className={styles.container}
            data-viewname={EVENTS.PRAY_EVENTS.VIEW_EVENT_NAMES.LOGIN_STEPS.LOGIN_OPTION}
          >
            <div className={styles.content}>
              <div className={styles.header}>
                {title && (
                  <div className={styles.title}>
                    <Text variant={typography.heading_xl} color={colors.text_primary}>
                      {title}
                    </Text>
                  </div>
                )}
                {getSubtitle() && (
                  <div className={styles.subtitle}>
                    <Text variant={typography.headline_small} color={colors.text_secondary}>
                      {getSubtitle()}
                    </Text>
                  </div>
                )}
              </div>
              <div className={styles.buttons}>
                <SocialAuth disabled={disabled} onAuthSuccess={() => onSuccess?.()} hideTopLabel>
                  <Button onClick={handleEmailLogin} data-tapeventname="Continue with Email">
                    <Mail width={24} height={24} className="mr-2" />
                    Continue with Email
                  </Button>
                </SocialAuth>
              </div>

              <div className={styles.terms}>
                <Text variant={typography.caption_medium} color={colors.text_secondary}>
                  By providing your email and signing up, you agree to the{' '}
                  <a href={PRAY_EXTERNAL_LINKS.TERMS_OF_SERVICE} target="_blank" rel="noreferrer">
                    Terms of Service
                  </a>
                  ,{' '}
                  <a href={PRAY_EXTERNAL_LINKS.PRIVACY_POLICY} target="_blank" rel="noreferrer">
                    Privacy Policy
                  </a>
                  , and confirm you are at least 16 years old.
                </Text>
              </div>
            </div>
          </div>
        );

      case Steps.EMAIL_CAPTURE:
        return (
          <EmailCapturePage
            data={{ email }}
            title="What's your email?"
            subtitle="Let's get you signed up."
            showSocialAuth={false}
            handleEmailValidation={handleEmailValidation}
            onDataChange={handleDataChange}
            onAuthSuccess={onSuccess}
          />
        );

      case Steps.MAGIC_LINK_SENT:
        return (
          <EmailConfirmationPage data={{ email }} handleEmailValidation={handleEmailValidation as unknown as () => Promise<void>} customStyles={styles} />
        );

      default:
        return null;
    }
  };

  return (
    <>
      {isShowAppBar && (
        <AppBar left={appBarLeftComponent} center={appBarCenterComponent} right={appBarRightComponent} />
      )}
      <div className={styles.wrapper}>{renderStep()}</div>
    </>
  );
}
